import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'react-toastify';
import { insertContent, finalizeSession } from '../api/notes';
import { useAuthReady } from '../hooks/useAuthReady';
import { getSessionUuid } from '../utils/identity';
import { deleteRequest, get, patch, post } from '../utils/apiClient';
import { isClientRole } from '../utils/roleConstants';

import { nowISO } from './../utils/dateUtils';
import { logger } from "../utils/logger";
import { buildCompletionMessage } from '../utils/notificationMessage';

export interface SoapProgressNotification {
  type: string;
  status: string;
  request_id?: string;
  message?: string;
  full_message?: string;
  note_data?: Record<string, unknown>;
  batch_notes?: Record<string, unknown>[];
  session_id?: string;
  note_id?: string;
  noteId?: string;
  content?: string;
  soap_content?: string;
  sessionId?: string;
  error_type?: string;
  [key: string]: unknown;
}

export interface NotificationItem {
  id: number | string;
  timestamp: string;
  read: boolean;
  type: string;
  source?: 'persisted' | 'local';
  title?: string;
  message?: string;
  link?: string;
  [key: string]: unknown;
}

export interface NotificationContextValue {
  notifications: NotificationItem[];
  unreadCount: number;
  addNotification: (notification: Partial<NotificationItem>) => number | string;
  markAsRead: (notificationId: number | string) => void;
  markAllAsRead: () => void;
  dismissNotification: (notificationId: number | string) => Promise<void>;
  snoozeNotification: (notificationId: number | string, until: string) => Promise<void>;
  addNoteCreatedNotification: (clientName: string, noteType: string, noteId: string) => number | string;
  soapProgress: SoapProgressNotification | null;
}

const NotificationContext = createContext<NotificationContextValue | null>(null);

export { NotificationContext };

// WebSocket close codes from backend
const WS_CLOSE_AUTH_REQUIRED = 4401; // Missing or expired token
const WS_CLOSE_AUTH_FORBIDDEN = 4403; // Invalid token or user mismatch

// Reconnection configuration
const RECONNECT_BASE_DELAY_MS = 1000;
const RECONNECT_MAX_DELAY_MS = 30000;
const RECONNECT_MAX_ATTEMPTS = 10;

type NotificationRoleContext = {
  role?: string;
  roles?: string[];
} | false | null | undefined;

function getStoredNotificationRoles(): string[] {
  try {
    const raw = sessionStorage.getItem('user');
    if (!raw || !raw.startsWith('{')) return [];
    const storedUser = JSON.parse(raw);
    if (!storedUser || typeof storedUser !== 'object') return [];
    const roles = Array.isArray(storedUser.roles)
      ? storedUser.roles.filter((role: unknown): role is string => typeof role === 'string')
      : [];
    return roles.length > 0 ? roles : (typeof storedUser.role === 'string' ? [storedUser.role] : []);
  } catch {
    return [];
  }
}

function getNotificationRoles(user: NotificationRoleContext): string[] {
  if (user) {
    const roles = Array.isArray(user.roles) ? user.roles : [];
    return roles.length > 0 ? roles : (user.role ? [user.role] : []);
  }
  return getStoredNotificationRoles();
}

/**
 * Return a log-safe summary of a WebSocket notification.
 * Never emit full note content, messages, or other PHI.
 */
function safeNotificationSummary(notification: SoapProgressNotification) {
  return {
    type: notification.type,
    status: notification.status,
    request_id: notification.request_id,
    session_id: notification.session_id,
    note_id: notification.note_id || notification.noteId || notification.note_data?.note_id || notification.note_data?.id,
    batch_count: Array.isArray(notification.batch_notes) ? notification.batch_notes.length : undefined,
    error_type: notification.error_type,
    has_content: !!(notification.content || notification.soap_content),
    has_full_message: !!notification.full_message,
  };
}

export function NotificationProvider({ children }: { children: React.ReactNode }) {
  const { ready, user } = useAuthReady();
  const notificationAuthRef = useRef({ ready, user });
  notificationAuthRef.current = { ready, user };
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [soapProgress, setSoapProgress] = useState(null);
  const [userUuid, setUserUuid] = useState(null);
  const wsRef = useRef(null);
  // Track seen request_ids to prevent duplicate notification processing
  const seenRequestIdsRef = useRef(new Set());
  // Reconnection state refs
  const intentionalCloseRef = useRef(false);
  const reconnectAttemptRef = useRef(0);
  const reconnectTimeoutRef = useRef(null);
  const expiryRefreshTimeoutsRef = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  const currentUuidRef = useRef(null);
  // Ref to hold openSocket function for use in scheduleReconnect (breaks circular dependency)
  const openSocketRef = useRef(null);
  // Track whether the initial inbox fetch for a given UUID has already completed,
  // so the HTTP mount fetch and the first WebSocket open for that UUID do not
  // duplicate each other, while reconnects still re-fetch via ws.onopen.
  const hasFetchedInboxForUuidRef = useRef<Record<string, boolean>>({});

  // Clear any pending reconnect timeout
  const clearReconnectTimeout = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    const sessionUuid = getSessionUuid(user);
    const userRoles = user ? (user.roles || (user.role ? [user.role] : [])) : [];
    const isClient = isClientRole(userRoles);
    if (!ready) return;
    if (sessionUuid && !isClient) {
      setUserUuid(sessionUuid);
    } else if (!isClient) {
      try {
        const raw = sessionStorage.getItem('user') || sessionStorage.getItem('user_uuid');
        if (raw) {
          const obj = (typeof raw === 'string' && raw.startsWith('{')) ? JSON.parse(raw) : raw;
          const fallbackUuid = (typeof obj === 'string') ? obj : (obj && obj.uuid) ? obj.uuid : null;
          const fallbackRoles = typeof obj === 'object' && obj
            ? (obj.roles || (obj.role ? [obj.role] : []))
            : [];
          const hasRoleContext = Array.isArray(fallbackRoles) && fallbackRoles.length > 0;
          if (fallbackUuid && hasRoleContext && !isClientRole(fallbackRoles)) {
            setUserUuid(fallbackUuid);
          }
        }
      } catch { /* ignore */ }
    }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  }, [ready, (user as any)?.uuid, (user as any)?.id, (user as any)?.impersonates_user_id]);

  // Schedule a reconnection attempt with exponential backoff
  const scheduleReconnect = useCallback((forUuid) => {
    // Don't reconnect if we've exceeded max attempts
    if (reconnectAttemptRef.current >= RECONNECT_MAX_ATTEMPTS) {
      logger.warn('[WS] Max reconnection attempts reached, giving up');
      return;
    }

    // Don't reconnect if page is hidden or offline
    if (document.visibilityState === 'hidden' || !navigator.onLine) {
      logger.log('[WS] Skipping reconnect - page hidden or offline');
      return;
    }

    // Prevent race condition: if a reconnect is already scheduled, don't schedule another
    if (reconnectTimeoutRef.current) {
      logger.log('[WS] Reconnect already scheduled, skipping duplicate');
      return;
    }

    // Calculate delay with exponential backoff
    const delay = Math.min(
      RECONNECT_BASE_DELAY_MS * Math.pow(2, reconnectAttemptRef.current),
      RECONNECT_MAX_DELAY_MS
    );
    
    logger.log(`[WS] Scheduling reconnect attempt ${reconnectAttemptRef.current + 1}/${RECONNECT_MAX_ATTEMPTS} in ${delay}ms`);
    
    reconnectTimeoutRef.current = setTimeout(() => {
      reconnectTimeoutRef.current = null; // Clear ref after timeout fires
      reconnectAttemptRef.current += 1;
      // Use ref to call openSocket to break circular dependency
      if (openSocketRef.current) {
        openSocketRef.current(forUuid, true);
      }
    }, delay);
  }, []);

  // Fetch persisted notifications from the inbox API (used on mount + reconnect)
  const fetchInbox = useCallback(async () => {
    const { ready: authReady, user: authUser } = notificationAuthRef.current;
    const roles = getNotificationRoles(authUser);
    if (!authReady || roles.length === 0 || isClientRole(roles)) return;

    try {
      const res = await get('/api/provider-notifications/inbox?limit=50');
      if (!res.ok) return;
      const data = await res.json();
      const persisted = (data.notifications || []).map((n: Record<string, unknown>) => ({
        id: n.id,
        timestamp: n.created_at as string,
        read: n.is_read as boolean,
        type: n.notification_type as string,
        source: 'persisted' as const,
        snoozed_until: n.snoozed_until as string | null,
        title: n.title as string,
        message: n.message as string,
        ...(n.extra_data as Record<string, unknown> || {}),
      }));
      setNotifications(prev => {
        const existingIds = new Set(prev.map(p => String(p.id)));
        const newItems = persisted.filter((p: NotificationItem) => !existingIds.has(String(p.id)));
        return [...prev, ...newItems];
      });
      setUnreadCount(data.unread_count ?? 0);
    } catch (e) {
      logger.error('[Notifications] Failed to fetch inbox:', e);
    }
  }, []);

  const openSocket = useCallback(async (forUuid, isReconnect = false) => {
    if (!forUuid) return;
    
    // Clear any pending reconnect timeout when opening a new connection
    clearReconnectTimeout();
    
    // Update current UUID ref
    currentUuidRef.current = forUuid;
    
    // Validate UUID format to prevent connection attempts with invalid UUIDs
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!uuidRegex.test(forUuid)) {
      logger.warn('[WS] Invalid UUID format, skipping connection:', forUuid);
      return;
    }
    
    // Close existing connection if open or connecting (mark as intentional)
    if (wsRef.current) {
      const state = wsRef.current.readyState;
      if (state === WebSocket.OPEN || state === WebSocket.CONNECTING) {
        intentionalCloseRef.current = true;
        try { wsRef.current.close(1000, 'Reconnecting for new UUID'); } catch { /* ignore close errors */ }
      }
    }
    
    // Fetch notification token for authenticated WebSocket connection
    let token;
    let wsUuid = forUuid;
    try {
      const response = await get('/api/auth/notification-token');
      if (!response.ok) {
        logger.error('[WS] Failed to get notification token:', response.status);
        // If auth failed (401/403), don't retry - user needs to re-authenticate
        if (response.status === 401 || response.status === 403) {
          logger.warn('[WS] Authentication failed, not scheduling reconnect');
          reconnectAttemptRef.current = RECONNECT_MAX_ATTEMPTS; // Stop retries
          return;
        }
        // For other errors, schedule a reconnect
        scheduleReconnect(forUuid);
        return;
      }
      const data = await response.json();
      token = data.token;
      wsUuid = data.user_id || forUuid;
      // The token's user_id is the canonical channel identity; prefer it over
      // any locally-derived UUID so the WebSocket handshake succeeds.
      currentUuidRef.current = wsUuid;
    } catch (error) {
      logger.error('[WS] Error fetching notification token:', error);
      // Network error - schedule reconnect
      scheduleReconnect(forUuid);
      return;
    }
    
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.host;
    const wsUrl = `${protocol}//${host}/ws/notifications/${wsUuid}?token=${encodeURIComponent(token)}`;
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      logger.log('[WS] connected for UUID:', wsUuid);
      // Reset reconnect attempts on successful connection
      reconnectAttemptRef.current = 0;
      clearReconnectTimeout();
      // Re-fetch inbox to catch notifications missed while disconnected.
      // On the first open for a UUID the mount/UUID-change HTTP effect handles the
      // initial load; on reconnects (or if the HTTP fetch somehow hasn't happened)
      // we fetch here and mark the UUID as loaded.
      if (isReconnect || !hasFetchedInboxForUuidRef.current[wsUuid]) {
        fetchInbox();
        hasFetchedInboxForUuidRef.current[wsUuid] = true;
      }
    };

    ws.onmessage = (event) => {
      try {
        const notification = JSON.parse(event.data);
        logger.log('Received WebSocket notification:', safeNotificationSummary(notification));

        if (notification.type === 'soap_progress') {
          logger.log('[SOAP_WS]', safeNotificationSummary(notification));
          
          // Deduplicate terminal notifications (complete/duplicate/error) by request_id
          const { status, request_id } = notification;
          if ((status === 'complete' || status === 'duplicate' || status === 'error') && request_id) {
            if (seenRequestIdsRef.current.has(request_id)) {
              logger.log('[SOAP_WS] Skipping duplicate notification for request_id:', request_id, 'status:', status);
              return;
            }
            seenRequestIdsRef.current.add(request_id);
            // Limit set size to prevent memory leaks (keep last 100 request_ids)
            if (seenRequestIdsRef.current.size > 100) {
              const firstKey = seenRequestIdsRef.current.values().next().value;
              seenRequestIdsRef.current.delete(firstKey);
            }
          }
          
          setSoapProgress(notification);

          if (notification.status === 'preamble') {
            logger.log('[SOAP_PREAMBLE] Dispatching preamble event request_id=%s', notification.request_id);
            window.dispatchEvent(new CustomEvent('soap_note_preamble', {
              detail: {
                message: notification.message,
                full_message: notification.full_message,
                requestId: notification.request_id,
                sessionId: notification.session_id,
              },
            }));
          }

          if (notification.status === 'complete') {
            const safeNoteId =
              notification.note_id ||
              notification.noteId ||
              (notification.note_data && (notification.note_data.note_id || notification.note_data.id));
            const completionMessage = buildCompletionMessage(notification.full_message, safeNoteId);

            if (completionMessage || notification.note_data) {
              logger.log('[SOAP_COMPLETE] Dispatching completion event request_id=%s batch_count=%d', notification.request_id, notification.batch_notes?.length || 0);
              window.dispatchEvent(new CustomEvent('soap_note_complete', {
                detail: {
                  message: completionMessage,
                  noteData: notification.note_data,
                  batchNotes: notification.batch_notes || null,
                  requestId: notification.request_id,
                  sessionId: notification.session_id
                }
              }));
            }
            
            (async () => {
              try {
                const nid =
                  notification.note_id ||
                  notification.noteId ||
                  (notification.note_data && (notification.note_data.note_id || notification.note_data.id));
                const content = notification.content || notification.soap_content || null;
                // Only call insert-content when there is content to insert —
                // a content-free call is a write on the canonical note row that
                // bumps updated_at and stales an open editor's version token.
                const noteUpdatedAt =
                  notification.note_data && (notification.note_data.updated_at || notification.note_data.updatedAt);
                if (nid && content) {
                  await insertContent(nid, content, noteUpdatedAt || undefined);
                }
                const sid =
                  notification.session_id ||
                  notification.sessionId ||
                  (notification.note_data && (notification.note_data.session_id || notification.note_data.sid));
                if (sid && !sid.includes(':')) {
                  await finalizeSession(sid);
                }
              } catch (e) {
                logger.error('SOAP finalize flow failed:', (e as Error)?.message || e);
              } finally {
                setTimeout(() => setSoapProgress(null), 5000);
              }
            })();
          } else if (notification.status === 'error') {
            // Display error message to user via toast
            const errorMessage = notification.message || 'Note generation failed. Please try again.';
            logger.error('[SOAP_ERROR] Note generation error:', notification.error_type, errorMessage);
            
            try {
              toast.error(errorMessage, {
                autoClose: 8000,
                position: 'top-center',
              });
            } catch (toastError) {
              logger.error('[SOAP_ERROR] Failed to show toast:', toastError);
            }
            
            // Dispatch error event for components like NoteAIChat to handle
            window.dispatchEvent(new CustomEvent('soap_note_error', {
              detail: {
                message: errorMessage,
                errorType: notification.error_type,
                requestId: notification.request_id,
                sessionId: notification.session_id,
              },
            }));
            
            setTimeout(() => setSoapProgress(null), 8000);
          }
        } else if (notification.type === 'note_supervision_submitted' || 
                   notification.type === 'note_changes_requested' || 
                   notification.type === 'note_co_signed') {
          addNotification(notification);
        } else {
          addNotification(notification);
        }
      } catch (error) {
        logger.error('Error parsing WebSocket message:', error);
      }
    };

    ws.onerror = (error) => {
      logger.error('[WS] error:', error);
      logger.error('WebSocket URL path that failed:', `/ws/notifications/${wsUuid}`);
    };

    ws.onclose = (event) => {
      logger.log('[WS] closed for UUID:', wsUuid, 'code:', event.code, 'reason:', event.reason);
      wsRef.current = null; // Clear ref so visibility/online handlers can reconnect

      // Don't reconnect if this was an intentional close
      if (intentionalCloseRef.current) {
        intentionalCloseRef.current = false;
        logger.log('[WS] Intentional close, not reconnecting');
        return;
      }

      // Don't reconnect if UUID has changed (another connection will be opened)
      if (currentUuidRef.current !== wsUuid) {
        logger.log('[WS] UUID changed, not reconnecting for old UUID');
        return;
      }

      // Don't reconnect on auth errors - user needs to re-authenticate
      if (event.code === WS_CLOSE_AUTH_REQUIRED || event.code === WS_CLOSE_AUTH_FORBIDDEN) {
        logger.warn('[WS] Auth error, not reconnecting. User may need to re-authenticate.');
        reconnectAttemptRef.current = RECONNECT_MAX_ATTEMPTS; // Stop retries
        return;
      }

      // Schedule reconnect for unexpected closes (network issues, server restart, token expiry)
      logger.log('[WS] Unexpected close, scheduling reconnect');
      scheduleReconnect(wsUuid);
    };
  }, [clearReconnectTimeout, scheduleReconnect, fetchInbox]);

  // Keep openSocketRef in sync with openSocket for use in scheduleReconnect
  useEffect(() => {
    openSocketRef.current = openSocket;
  }, [openSocket]);

  useEffect(() => {
    if (userUuid) {
      // Reset reconnect attempts when UUID changes
      reconnectAttemptRef.current = 0;
      openSocket(userUuid, false);
    }
    return () => {
      // Mark close as intentional before unmounting
      intentionalCloseRef.current = true;
      clearReconnectTimeout();
      if (wsRef.current) {
        try { wsRef.current.close(1000, 'Component unmounting'); } catch { /* ignore close errors */ }
      }
    };
  }, [userUuid, openSocket, clearReconnectTimeout]);

  // Fetch persisted notifications from the inbox API on mount / UUID change.
  // This decouples the initial load from the WebSocket handshake (so users behind
  // proxies that block WebSockets still see their inbox). The first ws.onopen for
  // a UUID skips its own fetch because this effect already covers it; reconnects
  // still re-fetch via ws.onopen to catch notifications missed while disconnected.
  useEffect(() => {
    if (!userUuid) return;
    if (!hasFetchedInboxForUuidRef.current[userUuid]) {
      hasFetchedInboxForUuidRef.current[userUuid] = true;
      fetchInbox();
    }
  }, [userUuid, fetchInbox]);

  useEffect(() => {
    const refreshOnReturn = () => {
      if (document.visibilityState === 'visible') {
        fetchInbox();
      }
    };
    document.addEventListener('visibilitychange', refreshOnReturn);
    window.addEventListener('focus', refreshOnReturn);
    return () => {
      document.removeEventListener('visibilitychange', refreshOnReturn);
      window.removeEventListener('focus', refreshOnReturn);
    };
  }, [fetchInbox]);

  useEffect(() => {
    const handleUuidChange = (e) => {
      const newUuid = e.detail?.uuid;
      if (newUuid && newUuid !== userUuid) {
        intentionalCloseRef.current = true;
        clearReconnectTimeout();
        reconnectAttemptRef.current = 0;
        setUserUuid(newUuid);
        openSocket(newUuid, false);
      }
    };
    window.addEventListener('user-uuid-changed', handleUuidChange);
    return () => window.removeEventListener('user-uuid-changed', handleUuidChange);
  }, [userUuid, openSocket, clearReconnectTimeout]);
  
  // Reconnect when page becomes visible or comes back online
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && userUuid && !wsRef.current) {
        logger.log('[WS] Page became visible, attempting reconnect');
        openSocket(userUuid, true);
      }
    };
    
    const handleOnline = () => {
      if (userUuid && !wsRef.current) {
        logger.log('[WS] Network came back online, attempting reconnect');
        openSocket(userUuid, true);
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('online', handleOnline);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', handleOnline);
    };
  }, [userUuid, openSocket]);
  
  const addNotification = (notification: Partial<NotificationItem>) => {
    const newNotification = {
      id: notification.id || Date.now(),
      timestamp: nowISO(),
      read: false,
      source: 'local' as const,
      ...notification
    };
    // Deduplicate: skip if a notification with this ID already exists (e.g. from inbox fetch)
    setNotifications(prev => {
      if (prev.some(n => String(n.id) === String(newNotification.id))) return prev;
      return [newNotification, ...prev];
    });
    setUnreadCount(prev => prev + 1);
    return newNotification.id;
  };
  
  const markAsRead = (notificationId: number | string) => {
    setNotifications(prev => {
      const updated = prev.map(n => n.id === notificationId ? { ...n, read: true } : n);
      setUnreadCount(updated.filter(n => !n.read).length);
      return updated;
    });
    // Persist read status to backend (fire-and-forget)
    patch(`/api/provider-notifications/${notificationId}/read`).catch(() => {});
  };
  
  const markAllAsRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
    // Persist read status to backend (fire-and-forget)
    patch('/api/provider-notifications/mark-all-read').catch(() => {});
  };

  const dismissNotification = async (notificationId: number | string) => {
    const notification = notifications.find(item => item.id === notificationId);
    if (notification?.source !== 'persisted') {
      setNotifications(prev => {
        const updated = prev.filter(n => n.id !== notificationId);
        setUnreadCount(updated.filter(n => !n.read).length);
        return updated;
      });
      return;
    }
    try {
      const response = await deleteRequest(`/api/provider-notifications/${notificationId}`);
      if (!response.ok) {
        throw new Error(`Dismiss failed (${response.status})`);
      }
      const result = await response.json() as { success?: boolean };
      if (!result.success) {
        throw new Error('Dismiss was not accepted');
      }
      setNotifications(prev => {
        const updated = prev.filter(n => n.id !== notificationId);
        setUnreadCount(updated.filter(n => !n.read).length);
        return updated;
      });
    } catch (error) {
      logger.error('[Notifications] Failed to dismiss notification:', error);
      toast.error('Could not dismiss this notification. Please try again.');
    }
  };

  const snoozeNotification = async (notificationId: number | string, until: string) => {
    const notification = notifications.find(item => item.id === notificationId);
    if (notification?.source !== 'persisted') {
      return;
    }
    try {
      const response = await post(`/api/provider-notifications/${notificationId}/snooze`, { until });
      const result = await response.json() as { success?: boolean };
      if (!result.success) {
        throw new Error('Snooze was not accepted');
      }
      setNotifications(prev => {
        const updated = prev.filter(n => n.id !== notificationId);
        setUnreadCount(updated.filter(n => !n.read).length);
        return updated;
      });
      const timerKey = String(notificationId);
      const existingTimer = expiryRefreshTimeoutsRef.current.get(timerKey);
      if (existingTimer) {
        clearTimeout(existingTimer);
      }
      const timer = setTimeout(() => {
        expiryRefreshTimeoutsRef.current.delete(timerKey);
        fetchInbox();
      }, Math.max(0, new Date(until).getTime() - Date.now()) + 100);
      expiryRefreshTimeoutsRef.current.set(timerKey, timer);
    } catch (error) {
      logger.error('[Notifications] Failed to snooze notification:', error);
      toast.error('Could not snooze this notification. Please try again.');
    }
  };

  useEffect(() => () => {
    expiryRefreshTimeoutsRef.current.forEach(timer => clearTimeout(timer));
    expiryRefreshTimeoutsRef.current.clear();
  }, []);
  
  const addNoteCreatedNotification = (clientName: string, noteType: string, noteId: string) => {
    return addNotification({
      type: 'note_created',
      title: 'Note Created',
      message: `${clientName} — ${noteType} is ready for review`,
      // Notification links are persisted, so don't bake in a return_to context.
      // The click-time context will be captured by the notification panel.
      link: `/notes/${encodeURIComponent(noteId)}`,
    });
  };

  return (
    <NotificationContext.Provider value={{ 
      notifications, 
      unreadCount, 
      addNotification, 
      markAsRead, 
      markAllAsRead,
      dismissNotification,
      snoozeNotification,
      addNoteCreatedNotification,
      soapProgress
    }}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications(): NotificationContextValue {
  try {
    const context = useContext(NotificationContext);
    return context ?? {
      notifications: [],
      unreadCount: 0,
      addNotification: () => 0,
      markAsRead: () => {},
      markAllAsRead: () => {},
      dismissNotification: async () => {},
      snoozeNotification: async () => {},
      addNoteCreatedNotification: () => 0,
      soapProgress: null,
    };
  } catch (error) {
    logger.warn('useNotifications: NotificationContext not available during render:', error);
    return {
      notifications: [],
      unreadCount: 0,
      addNotification: () => 0,
      markAsRead: () => {},
      markAllAsRead: () => {},
      dismissNotification: async () => {},
      snoozeNotification: async () => {},
      addNoteCreatedNotification: () => 0,
      soapProgress: null,
    };
  }
}
